import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/invoice_sending_options';

export const InvoiceSendingOptionsSchema = z.object({
  generateInvoiceInDebtorLocale: z.boolean(),
  defaultInvoiceTemplateId: z.string().nullable(),
  defaultCreditNoteTemplateId: z.string().nullable(),
  displayQRCode: z.boolean(),
});

export type InvoiceSendingOptions = z.infer<typeof InvoiceSendingOptionsSchema>;

export async function loadInvoiceSendingOptionsQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = InvoiceSendingOptionsSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'invoiceSendingOptions');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadInvoiceSendingOptions(options: QueryOptions<InvoiceSendingOptions> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<InvoiceSendingOptions, ApiError>({
    queryKey: ['invoiceSendingOptions'],
    queryFn: () => loadInvoiceSendingOptionsQueryFn(axiosInstance),
    ...options,
  });

  return addResourceNameToQueryResult<InvoiceSendingOptions, ApiError, 'invoiceSendingOptions'>(
    'invoiceSendingOptions',
    queryResult,
  );
}
